import React, { useEffect, useState } from "react";
import StockCountLine from "../../../../utilities/data/model/StockCountLine";

import {
  StockSelection,
  SelectionOptions,
  StockIdentifier,
  CurrentCountContextType,
} from "../../../../utilities/stock-count";
import { CurrentCountContext } from "../../../../utilities/stock-count/context";
import { useStockCountSearchContext } from "../../../../utilities/stock-count/hooks";
import { useStockAdjuster } from "../../screens/StockCountScreen/components/StockCountScreenRenderer/use-stock-adjuster";
import { useStockCounter } from "../../screens/StockCountScreen/hooks";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import Product from "../../../../utilities/data/model/Product";
import StockCount from "../../../../utilities/data/model/StockCount";

export interface CurrentCountProviderProps {
  children?: React.ReactNode;
  stockCount: StockCount;
}

export function CurrentCountProvider({
  children,
  stockCount,
}: CurrentCountProviderProps) {
  const searchContext = useStockCountSearchContext();

  const database = useDatabase();

  const { findStockCountLineByStockIdentifierOrCreate } = useStockCounter();

  const [stockSelection, setStockSelection] = useState<
    StockSelection | undefined
  >(undefined);

  const [previousStockSelection, setPreviousStockSelection] = useState<
    StockSelection | undefined
  >();

  const [selectedStockCountLine, setSelectedStockCountLine] = useState<
    StockCountLine | undefined
  >(undefined);

  async function updateSelectedStockCountLineByStockIdentifier(
    stockIdentifier: StockIdentifier,
    triggerUpdatedAt = false
  ) {
    try {
      if (!stockCount.id) {
        throw new Error("No stock count was selected");
      }

      const stockCountLine = await findStockCountLineByStockIdentifierOrCreate(
        stockCount.id,
        stockIdentifier
      );

      return updateStockCountLine(stockCountLine, triggerUpdatedAt);
    } catch (e) {
      console.log(e);
    }
  }

  async function updateStockSelection(
    selectionOptions: SelectionOptions,
    callback?: () => void
  ) {
    const { triggerUpdatedAt, ...stockSelection } = selectionOptions;

    setStockSelection((prev) => {
      setPreviousStockSelection(prev);
      stockAdjuster.updateForm(stockSelection, prev);
      return stockSelection;
    });

    if (stockSelection.updateFormOnly) {
      return;
    }

    const productId = stockSelection.stockIdentifier.productId;

    if (stockSelection.stockIdentifier.barcode) {
      searchContext.fullTextSearch.setQuery(
        stockSelection.stockIdentifier.barcode
      );
    } else if (productId) {
      database.collections
        .get<Product>("products")
        .find(productId)
        .then((product) => {
          searchContext.fullTextSearch.setQuery(product.name);
        })
        .catch(console.log);
    }

    await updateSelectedStockCountLineByStockIdentifier(
      stockSelection.stockIdentifier,
      selectionOptions.triggerUpdatedAt
    );
    callback?.();
  }

  async function updateStockCountLine(
    stockCountLine: StockCountLine,
    triggerUpdatedAt = false
  ) {
    setSelectedStockCountLine(stockCountLine);
    if (triggerUpdatedAt) {
      return stockCountLine.triggerUpdate();
    }
    return Promise.resolve();
  }

  const baseCurrentCountContext: Omit<
    CurrentCountContextType,
    "stockAdjuster"
  > = {
    stockCount: stockCount,
    stockSelection,
    selectedStockCountLine,
    previousStockSelection,

    resetSelections,
    updateStockSelection,
    updateSelectedStockCountLineByStockIdentifier,
    updateSelectedStockCountLine: updateStockCountLine,
  };

  const stockAdjuster = useStockAdjuster(baseCurrentCountContext);

  function resetState() {
    searchContext.fullTextSearch.clearSearchValue();
    resetSelections();
  }

  function resetSelections() {
    setStockSelection(undefined);
    setSelectedStockCountLine(undefined);
    setPreviousStockSelection(undefined);
  }

  useEffect(() => {
    searchContext.loadScanMode().then((scanMode) => {
      searchContext.updateScanMode(scanMode);
    });
  }, []);

  useEffect(() => {
    searchContext.setShowProductView(false);
  }, [stockSelection]);

  useEffect(() => {
    if (!searchContext.fullTextSearch.query) {
      searchContext.setShowProductView(false);
    }
  }, [searchContext.fullTextSearch.query]);

  useEffect(resetState, [searchContext.scanMode]);

  return (
    <CurrentCountContext.Provider
      value={{ ...baseCurrentCountContext, stockAdjuster }}
    >
      {children}
    </CurrentCountContext.Provider>
  );
}
